/**
 * GetStartedCard — the self-serve opt-in entry (PRD F6).
 *
 * Shown when a firm has no sessions yet. Surfaces the instant vault
 * readiness probe ("your Vault already answers quantitative DDQ
 * questions") and lets the firm choose its onboarding depth: upload a
 * DDQ now, take the guided interview, or bulk-import past DDQs.
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileUp, MessageSquareText, FolderInput, Database, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { queryKeys } from '@/api/queryKeys';
import { getOnboardingReadiness } from '@/api/endpoints';
import { cn } from '@/lib/utils';

export interface GetStartedCardProps {
  className?: string;
}

export function GetStartedCard({ className }: GetStartedCardProps) {
  const { data: readiness } = useQuery({
    queryKey: queryKeys.onboardingReadiness(),
    queryFn: getOnboardingReadiness,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const actions = [
    {
      key: 'upload',
      icon: FileUp,
      title: 'Upload a DDQ',
      description: readiness?.vault_ready
        ? 'Your Vault data answers quantitative questions instantly.'
        : 'Start with a questionnaire — gaps are explained, never silent.',
      to: '/upload',
      primary: true,
    },
    {
      key: 'interview',
      icon: MessageSquareText,
      title: 'Guided interview',
      description: 'Answer common DDQ questions once; reuse them everywhere.',
      to: '/knowledge-base',
      primary: false,
    },
    {
      key: 'import',
      icon: FolderInput,
      title: 'Import past DDQs',
      description: 'Seed your approved answer history in bulk.',
      to: '/upload',
      primary: false,
    },
  ];

  return (
    <Card className={cn('border-l-4 border-l-apx-accent', className)} data-testid="get-started-card">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-3 text-lg">
          <Database className="h-5 w-5 text-apx-accent" aria-hidden />
          Get started with APX Respond
          {readiness?.vault_ready && (
            <Badge variant="outline" className="gap-1 text-tier-exact border-tier-exact">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
              Vault data ready
            </Badge>
          )}
        </CardTitle>
        {readiness && (
          <p className="text-sm text-muted-foreground">
            {readiness.vault_ready
              ? `Respond already knows ${readiness.vault_fields_present} quantitative facts about your firm` +
                (readiness.narrative_count > 0
                  ? ` and has ${readiness.narrative_count} firm-authored narratives on file.`
                  : '.')
              : 'Choose how deep to go — every path leads to sourced, review-ready answers.'}
          </p>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 sm:grid-cols-3">
          {actions.map((action) => (
            <Link
              key={action.key}
              to={action.to}
              className={cn(
                'rounded-lg border p-4 transition-colors hover:bg-muted/50',
                action.primary && readiness?.suggested_next === action.key
                  ? 'border-apx-accent'
                  : 'border-border'
              )}
            >
              <action.icon className="mb-2 h-5 w-5 text-apx-accent" aria-hidden />
              <div className="font-medium">{action.title}</div>
              <p className="mt-1 text-sm text-muted-foreground">{action.description}</p>
            </Link>
          ))}
        </div>
        <div className="mt-4">
          <Button asChild>
            <Link to="/upload">
              <FileUp className="mr-2 h-4 w-4" aria-hidden />
              Upload your first DDQ
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
